import { z } from 'zod';

export const audienceLevelSchema = z.enum(['beginner', 'intermediate', 'advanced']);
export type AudienceLevel = z.infer<typeof audienceLevelSchema>;

export const conceptIdSchema = z.enum([
  'piece-power',
  'moves',
  'rules',
  'board-control',
  'defense',
  'development',
  'king-safety',
  'puzzles',
  'opening',
  'endgame',
  'attack',
  'crowning',
  'backgammon',
  'bridge',
  'whist',
  'rummy',
  'pirate',
  'colour-dash',
  'mahjong-solitaire',
  'hearts',
  'crazy-eights',
]);
export type ConceptId = z.infer<typeof conceptIdSchema>;

export const drillTaskSchema = z.enum(['legal-move', 'best-move', 'trade']);
export type DrillTask = z.infer<typeof drillTaskSchema>;

export const explainStepSchema = z.object({
  kind: z.literal('explain'),
  title: z.string().min(1),
  body: z.string().min(1),
  image: z.string().optional(),
  narration: z.string().optional(),
});
export type ExplainStep = z.infer<typeof explainStepSchema>;

export const demoStepSchema = z.object({
  kind: z.literal('demo'),
  title: z.string().min(1),
  body: z.string().min(1),
  fen: z.string().min(1),
  highlight: z
    .object({ from: z.string().min(2), to: z.string().min(2), check: z.boolean().optional() })
    .optional(),
});
export type DemoStep = z.infer<typeof demoStepSchema>;

export const drillStepSchema = z.object({
  kind: z.literal('drill'),
  title: z.string().min(1),
  body: z.string().min(1),
  fen: z.string().min(1),
  task: drillTaskSchema,
  /** Best-move/trade eval tolerance in centipawns. Default 150 when absent. */
  tolerance: z.number().nonnegative().optional(),
  /** Acceptable SAN answers (pre-authored offline grading key). */
  answer: z.array(z.string()).min(1).optional(),
  hint: z.string().optional(),
  /** Free mode: any legal move passes (used by board-control live heatmap drill). */
  free: z.boolean().optional(),
  /** Progressive hints, most → least vague, shown one rung per wrong attempt. */
  hintLadder: z.array(z.string().min(1)).optional(),
  /** Difficulty tier 1-3 for puzzle drills (1 easiest, 3 hardest). */
  tier: z.number().int().min(1).max(3).optional(),
  /** Full solution line in SAN; `solution[0]` must be in `answer`. */
  solution: z.array(z.string().min(1)).optional(),
  /** Puzzle outcome the solution must reach. */
  expect: z.enum(['mate', 'win-material']).optional(),
});
export type DrillStep = z.infer<typeof drillStepSchema>;

export const quizQuestionSchema = z.object({
  prompt: z.string().min(1),
  choices: z.array(z.string()).min(2),
  correct: z.number().int().nonnegative(),
});
export type QuizQuestion = z.infer<typeof quizQuestionSchema>;

export const quizStepSchema = z.object({
  kind: z.literal('quiz'),
  title: z.string().min(1),
  body: z.string().min(1),
  questions: z.array(quizQuestionSchema).min(1),
});
export type QuizStep = z.infer<typeof quizStepSchema>;

export const stepSchema = z.discriminatedUnion('kind', [
  explainStepSchema,
  demoStepSchema,
  drillStepSchema,
  quizStepSchema,
]);
export type Step = z.infer<typeof stepSchema>;

export const lessonSchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  title: z.string().min(1),
  audienceLevel: audienceLevelSchema,
  concepts: z.array(conceptIdSchema).min(1),
  steps: z.array(stepSchema).min(1),
  quiz: quizStepSchema.optional(),
  coverImage: z.string().optional(),
});
export type Lesson = z.infer<typeof lessonSchema>;
